import { useState, memo } from "react";
import {
  OutlinedInput,
  FormControl,
  FormControlProps,
  InputAdornment,
  IconButton,
  OutlinedInputProps,
  Typography,
} from "@mui/material";
import classes from "./styles.module.scss";
import clsx from "clsx";
import ErrorMessage from "../../text/ErrorMessage";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@/components/icons/VisibilityIcon";
import VisibilityOffIcon from "@/components/icons/VisibilityOffIcon";
import { useTranslations } from "next-intl";

interface InputsProps extends OutlinedInputProps {
  title?: string;
  titleRequired?: boolean;
  placeholder?: string;
  name?: string;
  type?: string;
  defaultValue?: string;
  value?: string | number;
  showEyes?: boolean;
  root?: string;
  className?: any;
  inputRef?: any;
  autoComplete?: string;
  errorMessage?: string | null;
  optional?: boolean;
  infor?: string;
  isShowError?: boolean;
  rootProps?: FormControlProps;
  styleTitle?: {
    fontSize: string;
    fontWeight: number;
    lineHeight: string;
    letterSpacing: string;
  };
}
const InputTextfield = memo(function InputTextField(props: InputsProps) {
  const t = useTranslations("messages");

  const [toggleEyes, setToggleEyes] = useState(false);
  const {
    title,
    placeholder,
    name,
    defaultValue,
    value,
    type,
    root,
    className,
    showEyes,
    inputRef,
    errorMessage,
    autoComplete,
    optional,
    infor,
    titleRequired,
    isShowError,
    rootProps,
    styleTitle,
    ...rest
  } = props;

  const handleClick = () => {
    setToggleEyes(!toggleEyes);
  };

  const { ref: refInput, ...inputProps } = inputRef || { ref: null };

  return (
    <FormControl className={clsx(classes.root, root)} {...rootProps}>
      {title && (
        <Typography
          sx={{
            fontFamily: "Montserrat",
            color: !!errorMessage || isShowError ? "var(--cimigo-danger)" : "var(--gray-80)",
            fontSize: styleTitle?.fontSize || 14,
            fontWeight: styleTitle?.fontWeight || 500,
            lineHeight: styleTitle?.lineHeight || "24px",
          }}
        >
          {title}
          {optional ? (
            <span className={classes.optional} translation-key="common_optional"> ({t("common_optional")})</span>
          ) : (
            ""
          )}
          {titleRequired ? (
            <span className={classes.titleRequired}> *</span>
          ) : (
            ""
          )}
        </Typography>
      )}
      <OutlinedInput
        type={!toggleEyes ? type : "text"}
        placeholder={placeholder}
        fullWidth
        name={name}
        defaultValue={defaultValue}
        value={value}
        variant="standard"
        classes={{
          root: clsx(classes.inputTextfield, {
            [classes.inputInvalid]: !!errorMessage || isShowError,
          }),
        }}
        onKeyPress={
          type === "number"
            ? (e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }
            : undefined
        }
        className={className}
        autoComplete={autoComplete}
        onWheel={(e: any) =>
          e.target instanceof HTMLElement &&
          (e.target as any).type === "number" &&
          e.target.blur()
        }
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: !!errorMessage || isShowError ? "var(--cimigo-danger)" : "var(--gray-30)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: !!errorMessage || isShowError ? "var(--cimigo-danger)" : "var(--gray-30)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: !!errorMessage || isShowError ? "var(--cimigo-danger)" : "var(--cimigo-blue)",
          },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        }}
        endAdornment={
          !errorMessage ? (
            showEyes && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClick}
                  className={classes.iconEye}
                  tabIndex={-1}
                >
                  {toggleEyes ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          ) : (
            <ErrorOutlineIcon className={classes.iconErrorOutline} />
          )
        }
        {...inputProps}
        inputRef={refInput}
        {...rest}
      />
      {infor && <p className={classes.textInfor}>{infor}</p>}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </FormControl>
  );
});
export default InputTextfield;
